/**
 * AppShell.tsx — Layout wrapper (N-19 sidebar migration + authed-shell polish).
 *
 * Decides which chrome to render based on the portal context:
 *   - public → full top header (brand + Beranda / Game / Worksheet + Masuk).
 *     This is the only place a top navbar appears, so visitors always see it
 *     on the landing / catalog / worksheets / join pages. On mobile (<768px)
 *     the horizontal links collapse into a hamburger that opens a drawer.
 *   - admin / tutor / student / parent → sidebar only on desktop + tablet;
 *     mobile (<768px) shows a thin trigger bar with just a hamburger + brand
 *     so the drawer can be opened. The top header is otherwise hidden so the
 *     dashboard does not double up on navigation.
 *
 * Hamburger button visibility:
 *   - desktop (≥1024px): hidden (sidebar already visible / top nav has room)
 *   - tablet (768-1023px): hidden (sidebar already visible, may be collapsed;
 *     public top nav has its own horizontal links)
 *   - mobile (<768px): visible (drawer is the only navigation surface)
 *
 * The actual breakpoint behavior is CSS-driven; this component only wires
 * the hamburger button presence + drawer open/close state.
 */

import type { JSX, ReactNode } from 'react';
import { useRef, useState } from 'react';
import { getHomeHref, type PortalContext, type ShellUser } from '../../lib/navigation.js';
import { NavIcon } from './NavIcon.js';
import { AccountMenu } from './AccountMenu.js';
import { Sidebar } from './Sidebar.js';
import { SidebarDrawer } from './SidebarDrawer.js';
import { PublicMobileDrawer } from './PublicMobileDrawer.js';

export interface AppShellProps {
  context: PortalContext;
  pathname: string;
  hash?: string;
  user: ShellUser | null;
  children: ReactNode;
  /**
   * Bare mode: no sidebar, header, footer or drawers — only the main
   * content. Used by the RC room's `?view=game` share-screen tab, where
   * nothing but the game may appear on the shared screen.
   */
  bare?: boolean;
}

export function AppShell({ context, pathname, hash = '', user, children, bare = false }: AppShellProps): JSX.Element {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [publicMenuOpen, setPublicMenuOpen] = useState(false);
  const hamburgerRef = useRef<HTMLButtonElement | null>(null);
  const publicHamburgerRef = useRef<HTMLButtonElement | null>(null);

  const isAuthed = context !== 'public';
  const homeHref = getHomeHref(context);

  if (bare) {
    return (
      <div className="app-shell app-shell--bare" data-context={context} data-testid="app-shell-bare">
        <main id="main-content" className="app-shell-main app-shell-main--bare" data-context={context}>
          {children}
        </main>
      </div>
    );
  }

  return (
    <div className="app-shell" data-context={context}>
      <a href="#main-content" className="skip-link">
        Lewati ke konten
      </a>

      {/* Public visitors: full top header. This is the only context that
          renders the horizontal navigation, so landing / catalog pages keep
          the navbar. Authed contexts skip this entirely.
          On mobile (<768px) the horizontal links collapse into a hamburger
          that opens <PublicMobileDrawer>. */}
      {context === 'public' && (
        <header className="app-header app-header-public" data-context="public">
          <div className="app-header-inner">
            <button
              ref={publicHamburgerRef}
              type="button"
              className="public-header-hamburger"
              aria-label="Buka menu"
              aria-expanded={publicMenuOpen}
              aria-controls="public-mobile-drawer"
              onClick={() => setPublicMenuOpen(true)}
              data-testid="public-header-hamburger"
            >
              <NavIcon icon="menu" size={22} />
            </button>
            <a className="app-header-brand" href={homeHref}>
              <img
                className="brand-logo"
                src="/images/brand/coding-class-logo-purple.png"
                alt="Coding Class TerusMengajar"
                width={44}
                height={25}
              />
              <span className="brand-divider" aria-hidden="true" />
              <span className="brand-product">Coding Class</span>
              <PublicInvitationBadge />
            </a>
            <PublicTopNav pathname={pathname} />
            <div className="app-header-actions">
              <AccountMenu user={user} context={context} />
            </div>
          </div>
        </header>
      )}

      {/* Authed mobile: minimal trigger bar. Only the hamburger + brand show
          here; the profile + logout live inside the drawer / sidebar. Hidden
          on tablet+ via CSS so desktop and tablet users see sidebar only. */}
      {isAuthed && (
        <header className="app-header app-header-mobile" data-context={context}>
          <div className="app-header-inner">
            <button
              ref={hamburgerRef}
              type="button"
              className="app-header-hamburger"
              aria-label="Buka menu"
              aria-expanded={drawerOpen}
              aria-controls="sidebar-drawer"
              onClick={() => setDrawerOpen(true)}
              data-testid="app-header-hamburger"
            >
              <NavIcon icon="menu" size={22} />
            </button>
            <a className="app-header-brand" href={homeHref}>
              <img
                className="brand-logo"
                src="/images/brand/coding-class-logo-purple.png"
                alt="Coding Class TerusMengajar"
                width={36}
                height={20}
              />
              <span className="brand-divider" aria-hidden="true" />
              <span className="brand-product">Coding Class</span>
            </a>
          </div>
        </header>
      )}

      <div className="app-shell-body">
        {isAuthed && (
          <Sidebar
            context={context}
            pathname={pathname}
            hash={hash}
            user={user}
            defaultCollapsed={false}
          />
        )}
        <main
          id="main-content"
          className={`app-shell-main ${isAuthed ? 'has-sidebar' : ''}`}
          data-context={context}
        >
          {children}
        </main>
      </div>

      <footer className="app-shell-footer">
        <a href="https://terusmengajar.id" target="_blank" rel="noreferrer">
          © TerusMengajar · terusmengajar.id
        </a>
      </footer>

      {/* Authed drawer: only for non-public contexts. */}
      {isAuthed && (
        <SidebarDrawer
          context={context}
          pathname={pathname}
          hash={hash}
          user={user}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          triggerRef={hamburgerRef}
        />
      )}

      {/* Public mobile drawer: hamburger replacement for the hidden horizontal
          nav on mobile. Public-only (authed contexts use the sidebar). */}
      {context === 'public' && (
        <PublicMobileDrawer
          pathname={pathname}
          open={publicMenuOpen}
          onClose={() => setPublicMenuOpen(false)}
          triggerRef={publicHamburgerRef}
        />
      )}
    </div>
  );
}

/**
 * PublicInvitationBadge — Small "INVITATION-ONLY" pill beside the Coding Class
 * brand mark in the public navbar. Signals to visitors that the program is
 * invitation-only. Rendered only inside the public context branch of
 * AppShell, so logged-in users never see it.
 *
 * On mobile (<768px) the pill collapses to a compact "info" icon — the same
 * message, less real estate. Tooltip + aria-label still explain what it means
 * for screen readers and on tap.
 */
function PublicInvitationBadge(): JSX.Element {
  return (
    <span
      className="public-invitation-badge"
      title="Program ini invitation-only. Login dengan kode undangan dari tutor."
      aria-label="Program ini invitation-only. Login dengan kode undangan dari tutor."
      data-testid="public-invitation-badge"
    >
      <span className="public-invitation-badge-text">Invitation-only</span>
      <span className="public-invitation-badge-icon" aria-hidden="true">
        <NavIcon icon="info" size={14} />
      </span>
    </span>
  );
}

/**
 * PublicTopNav — Horizontal nav for unauthenticated visitors (Beranda /
 * Game / Worksheet). Kept as a thin component so the public header doesn't
 * inherit the authed-only sidebar nav. Hidden on mobile (<768px) — the
 * hamburger + <PublicMobileDrawer> take over there.
 */
function PublicTopNav({ pathname }: { pathname: string }): JSX.Element {
  const links = [
    { label: 'Beranda', href: '/', exact: true },
    { label: 'Game', href: '/games' },
    { label: 'Worksheet', href: '/worksheets' },
  ];
  return (
    <nav className="public-top-nav" aria-label="Navigasi utama">
      {links.map((link) => {
        const active = link.exact ? pathname === link.href : pathname.startsWith(link.href);
        return (
          <a
            key={link.label}
            href={link.href}
            className={`public-top-nav-link ${active ? 'is-active' : ''}`}
            aria-current={active ? 'page' : undefined}
          >
            {link.label}
          </a>
        );
      })}
    </nav>
  );
}
